
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BookOpen, Users, Shield, Calendar, Camera, Settings } from "lucide-react";

const HelpEn = () => {
  const { t } = useTranslation(['common']);

  return (
    <div className="min-h-screen bg-uplivion-lightBlue/30 py-8">
      <div className="container mx-auto px-6 max-w-4xl">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="mb-8">
            <Link to="/" className="text-uplivion-blue hover:underline mb-4 inline-block">
              ← Back to Home
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Help & Guides</h1>
            <p className="text-xl text-gray-600">Everything you need to know to use uplivion optimally.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="space-y-6">
              <div className="border rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3 mb-4">
                  <Calendar className="w-8 h-8 text-uplivion-blue" />
                  <h2 className="text-xl font-semibold">Creating Your Timeline</h2>
                </div>
                <p className="text-gray-600 mb-4">
                  Learn how to create your first timeline and structure your life story.
                </p>
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm">Getting Started:</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>1. After registration, a timeline is automatically created for you</li>
                    <li>2. The timeline is divided into life phases (Childhood, Youth, etc.)</li>
                    <li>3. Click on a phase to expand it</li>
                    <li>4. Add your first memories</li>
                  </ul>
                </div>
              </div>

              <div className="border rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3 mb-4">
                  <Camera className="w-8 h-8 text-uplivion-orange" />
                  <h2 className="text-xl font-semibold">Adding Memories</h2>
                </div>
                <p className="text-gray-600 mb-4">
                  How to add photos, texts, and details to your most important life moments.
                </p>
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm">Creating a Memory:</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Click on "+" in a life phase</li>
                    <li>• Enter a title and description</li>
                    <li>• Choose a date (optional specific month/day)</li>
                    <li>• Upload photos (multiple possible)</li>
                    <li>• Set privacy settings</li>
                  </ul>
                </div>
              </div>

              <div className="border rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3 mb-4">
                  <Users className="w-8 h-8 text-green-600" />
                  <h2 className="text-xl font-semibold">Friends & Sharing</h2>
                </div>
                <p className="text-gray-600 mb-4">
                  Connect with friends and share selected memories.
                </p>
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm">Adding Friends:</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Go to the Friends section</li>
                    <li>• Search by username or email</li>
                    <li>• Send a friend request</li>
                    <li>• Accept incoming requests</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="border rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3 mb-4">
                  <Shield className="w-8 h-8 text-purple-600" />
                  <h2 className="text-xl font-semibold">Privacy Settings</h2>
                </div>
                <p className="text-gray-600 mb-4">
                  Control who can see which of your memories.
                </p>
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm">Visibility Options:</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• <strong>Only Me:</strong> Private, only you can see it</li>
                    <li>• <strong>Friends:</strong> All your friends can see it</li>
                    <li>• <strong>Public:</strong> Everyone can see it (coming soon)</li>
                  </ul>
                </div>
              </div>

              <div className="border rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-3 mb-4">
                  <Settings className="w-8 h-8 text-gray-600" />
                  <h2 className="text-xl font-semibold">Account Management</h2>
                </div>
                <p className="text-gray-600 mb-4">
                  Manage your profile, settings, and notifications.
                </p>
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm">Edit Profile:</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Go to your profile</li>
                    <li>• Click "Edit Profile"</li>
                    <li>• Update your information</li>
                    <li>• Change your profile picture</li>
                  </ul>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <BookOpen className="w-8 h-8 text-blue-600" />
                  <h2 className="text-xl font-semibold text-blue-900">Beta Features</h2>
                </div>
                <p className="text-blue-800 mb-4">
                  As a beta user, you have access to new features before they're available to everyone.
                </p>
                <div className="space-y-2">
                  <h3 className="font-semibold text-sm text-blue-900">Upcoming Features:</h3>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Timeline templates and themes</li>
                    <li>• Advanced search and filter functions</li>
                    <li>• Export functions (PDF, book)</li>
                    <li>• Family timelines</li>
                    <li>• Mobile app for iOS/Android</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">Common Problems & Solutions</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold mb-2">Photos won't upload</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Check your internet connection</li>
                  <li>• Maximum 10MB per photo</li>
                  <li>• Supported formats: JPG, PNG, WEBP</li>
                  <li>• Try a different browser</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Timeline won't load</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Refresh the page (F5)</li>
                  <li>• Clear browser cache</li>
                  <li>• Temporarily disable adblocker</li>
                  <li>• Use a modern browser</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Can't find friends</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Search for the exact username</li>
                  <li>• Check spelling</li>
                  <li>• Try the email address</li>
                  <li>• Ask friends to search for you</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Not receiving notifications</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Check your spam folder</li>
                  <li>• Review email settings</li>
                  <li>• Add uplivion.life to your contacts</li>
                  <li>• Check notification settings</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold mb-4">Need More Help?</h2>
            <p className="text-gray-600 mb-6">
              Couldn't find the answer to your question? Our support team is happy to help.
            </p>
            <div className="flex justify-center gap-4">
              <Button asChild>
                <Link to="/contact-en">Contact Us</Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/faq-en">Browse FAQ</Link>
              </Button>
            </div>
          </div>

          <div className="pt-8 border-t">
            <Button asChild>
              <Link to="/">Back to Home</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpEn;
